import React from 'react';
import clsx from 'clsx';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  /** Visual-only flag; HTML validation stays with the browser / caller. */
  invalid?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, invalid, id, className, ...props }, ref) => {
    const autoId = React.useId();
    const fieldId = id ?? autoId;
    const isInvalid = invalid || Boolean(error);
    return (
      <div className={clsx('flex flex-col gap-1.5', className)}>
        {label && (
          <label
            htmlFor={fieldId}
            className="text-[11px] font-medium tracking-wider uppercase text-slate-400"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={fieldId}
          aria-invalid={isInvalid || undefined}
          aria-describedby={
            error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined
          }
          className={clsx(
            'w-full bg-surface-2 border rounded-ds-lg text-slate-50 px-3 py-2 text-[13px] outline-none transition-colors duration-150',
            'placeholder:text-slate-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            isInvalid
              ? 'border-danger focus:border-danger'
              : 'border-white/[0.08] focus:border-accent',
          )}
          {...props}
        />
        {error ? (
          <p
            id={`${fieldId}-error`}
            className="text-[11px] text-danger-text"
          >
            {error}
          </p>
        ) : hint ? (
          <p
            id={`${fieldId}-hint`}
            className="text-[11px] text-slate-400"
          >
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
